import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
} from "../components/ui/drawer";
import { useCart } from "../context/CartContext";
import { ImageBroken, Check, ShoppingCartSimple, Minus, Plus } from "@phosphor-icons/react";

// 40'HQ qty: explicit override wins, else 65,000,000 cm³ × pcs ÷ (L×W×H).
function qty40(e) {
  if (e.qty_40ft != null) return Number(e.qty_40ft);
  const l = Number(e.carton_length), w = Number(e.carton_width), h = Number(e.carton_height), p = Number(e.pcs_per_ctn);
  if (!l || !w || !h || !p) return null;
  const q = Math.floor((65_000_000 * p) / (l * w * h));
  return q > 0 ? q : null;
}

export function ProductSheet({ product, open, onOpenChange, onNeedQuote }) {
  const { t } = useTranslation();
  const { activeCart, addToCart } = useCart();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  // iter 273: sample qty (pcs) per size, written to cart_item.sample_qty on add.
  const [qtys, setQtys] = useState({});

  if (!product) return null;

  const setQty = (id, v) => setQtys((q) => ({ ...q, [id]: Math.max(0, Number.isNaN(Number(v)) ? 0 : Math.floor(Number(v))) }));
  const chosen = product.entries.filter((e) => (qtys[e.entry_id] || 0) > 0);
  const totalSamples = chosen.reduce((s, e) => s + (qtys[e.entry_id] || 0), 0);

  const handleAdd = async () => {
    if (!activeCart) {
      onOpenChange(false);
      onNeedQuote();
      return;
    }
    setAdding(true);
    // Sizes with a sample qty are added with it; if none entered, add all sizes at 0.
    const targets = chosen.length ? chosen : product.entries;
    const sampleQtys = Object.fromEntries(chosen.map((e) => [e.entry_id, qtys[e.entry_id]]));
    await addToCart(product.product_id, targets.map((e) => e.entry_id), null, sampleQtys);
    setAdding(false);
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
      setQtys({});
      onOpenChange(false);
    }, 700);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mobile-app rounded-t-[28px] border-[#E7E5E4] max-h-[88vh]" data-testid="mobile-product-sheet">
        <div className="overflow-y-auto">
          <div className="aspect-[4/3] bg-[#FAFAF9] flex items-center justify-center mx-5 mt-1 rounded-[18px] overflow-hidden">
            {product.photo_url ? (
              <img src={product.photo_url} alt={product.description} className="w-full h-full object-cover" />
            ) : (
              <ImageBroken size={40} className="text-[#E7E5E4]" />
            )}
          </div>
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-display text-xl text-[#1C1917] leading-snug">{product.description || "-"}</DrawerTitle>
            {product.specification && <p className="text-sm text-[#78716C] mt-1">{product.specification}</p>}
            <p className="text-xs text-[#78716C] mt-1">{t("samples.stock")}: <span className={`font-mono font-semibold ${(product.sample_qty || 0) < 0 ? "text-red-600" : "text-[#1E3A8A]"}`} data-testid="mobile-sheet-sample-qty">{product.sample_qty || 0}</span></p>
          </DrawerHeader>

          <div className="px-5 pb-4">
            <p className="text-xs font-semibold tracking-wide text-[#78716C] uppercase mb-2">
              {t("mobile.sizes_available", { count: product.entries.length })}
            </p>
            <div className="rounded-[16px] border border-[#E7E5E4] divide-y divide-[#F1F0EE] overflow-hidden">
              {product.entries.map((e) => {
                const q = qtys[e.entry_id] || 0;
                return (
                <div key={e.entry_id} className="px-4 py-3 bg-white" data-testid={`mobile-product-sheet-variant-${e.entry_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1C1917] truncate">{e.size || e.item_no}</p>
                      <p className="text-xs text-[#78716C] mt-0.5 truncate" data-testid={`mobile-sheet-ci-${e.entry_id}`}>{e.item_no} &middot; {t("mobile.stock")}: {e.ci_available ?? e.ci_inventory_qty ?? "-"}{e.ci_reserved_total ? <span className="text-amber-700"> (−{e.ci_reserved_total} {t("ci.reserved")}{e.ci_reserved_mine ? `, ${t("ci.mine_short")} ${e.ci_reserved_mine}` : ""})</span> : null}</p>
                    </div>
                    <p className="text-sm font-semibold text-[#1E3A8A] shrink-0">
                      {e.suggested_price_usd != null ? `$${Number(e.suggested_price_usd).toFixed(2)}` : "-"}
                    </p>
                  </div>
                  {/* iter 266: packing & cost details */}
                  <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]" data-testid={`mobile-sheet-details-${e.entry_id}`}>
                    <dt className="text-[#A8A29E]">{t("mobile.carton_dims")}</dt>
                    <dd className="font-mono text-[#1C1917] text-right">{e.carton_length && e.carton_width && e.carton_height ? `${e.carton_length}×${e.carton_width}×${e.carton_height}` : "-"}</dd>
                    <dt className="text-[#A8A29E]">{t("mobile.pcs_per_ctn_label")}</dt>
                    <dd className="font-mono text-[#1C1917] text-right">{e.pcs_per_ctn ?? "-"}</dd>
                    <dt className="text-[#A8A29E]">{t("mobile.qty_40hq")}</dt>
                    <dd className="font-mono text-[#1C1917] text-right">{qty40(e) != null ? qty40(e).toLocaleString() : "-"}</dd>
                    <dt className="text-[#A8A29E]">{t("mobile.rmb_price")}</dt>
                    <dd className="font-mono text-[#1C1917] text-right">{e.purchase_price != null ? `¥${Number(e.purchase_price).toFixed(2)}` : "-"}</dd>
                  </dl>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-[#78716C]">{t("samples.sample_qty")}</span>
                    <div className="flex items-center gap-1.5" data-testid={`mobile-sheet-qty-${e.entry_id}`}>
                      <button type="button" onClick={() => setQty(e.entry_id, q - 1)} data-testid={`mobile-sheet-qty-minus-${e.entry_id}`}
                        className="press-scale w-8 h-8 rounded-full bg-[#FAFAF9] border border-[#E7E5E4] flex items-center justify-center text-[#78716C]">
                        <Minus size={12} weight="bold" />
                      </button>
                      <input type="number" inputMode="numeric" min="0" value={q === 0 ? "" : q} placeholder="0"
                        onChange={(ev) => setQty(e.entry_id, ev.target.value)}
                        data-testid={`mobile-sheet-qty-input-${e.entry_id}`}
                        className="w-14 h-8 text-center text-sm font-semibold text-[#1C1917] rounded-lg border border-[#E7E5E4] bg-white focus:outline-none focus:border-[#1E3A8A]" />
                      <button type="button" onClick={() => setQty(e.entry_id, q + 1)} data-testid={`mobile-sheet-qty-plus-${e.entry_id}`}
                        className="press-scale w-8 h-8 rounded-full bg-[#1E3A8A] flex items-center justify-center text-white">
                        <Plus size={12} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            {chosen.length > 0 && (
              <p className="text-xs text-[#78716C] mt-2 text-right" data-testid="mobile-sheet-qty-summary">
                {t("mobile.sample_qty_summary", { sizes: chosen.length, pcs: totalSamples })}
              </p>
            )}
          </div>

          <div className="px-5 pb-8 pt-1 safe-bottom">
            <button
              onClick={handleAdd}
              disabled={adding}
              data-testid="mobile-product-sheet-add-button"
              className={`press-scale w-full h-14 rounded-[16px] font-semibold text-base flex items-center justify-center gap-2 transition-colors duration-200 ${
                added ? "bg-green-600 text-white" : "bg-[#1E3A8A] text-white hover:bg-[#182f6e]"
              } disabled:opacity-70`}
            >
              {added ? (
                <>
                  <Check size={18} weight="bold" /> {t("mobile.added")}
                </>
              ) : (
                <>
                  <ShoppingCartSimple size={18} weight="bold" />
                  {activeCart ? t("mobile.add_to", { name: activeCart.name }) : t("mobile.choose_quote_to_add")}
                </>
              )}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
